import { client, expectNoContent, getAssistantWatering, getSpecies, getWeather, unwrap } from "@/app/lib/client";
import { computeSunHoursForDay } from "@/lib/sun-hours";
import { type Garden, type Location, type Pot, type PotSize, type Species, type TimelineKey, type WateringPlan, type WeatherForCurrent5DaysWindow } from "@/types";
import React, { createContext, useContext, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useToast } from "../components/system/Toast";

type PotPlanEntry = {
    plan: WateringPlan | null;
    refreshing: boolean;
    /** The last fetch failed. A previously-good `plan` is kept for stale-while-error. */
    error: boolean;
};

type PotPlansById = Record<string, PotPlanEntry>;

type DataContextType = {
    potsState: [Pot[], Dispatch<SetStateAction<Pot[]>>];
    locationState: [Location | null, Dispatch<SetStateAction<Location | null>>];
    weatherState: [WeatherForCurrent5DaysWindow | null, Dispatch<SetStateAction<WeatherForCurrent5DaysWindow | null>>];
    speciesState: [Species[], Dispatch<SetStateAction<Species[]>>];
    wateringPlansState: [PotPlansById, Dispatch<SetStateAction<PotPlansById>>];
}

const DataContext = createContext<DataContextType | null>(null);

export function DataContextProvider({ children, garden }: Readonly<{ children: React.ReactNode, garden: Garden }>) {

    const toast = useToast();

    const potsState = useState<Pot[]>(garden.pots);
    const locationState = useState<Location | null>(garden.location);
    const weatherState = useState<WeatherForCurrent5DaysWindow | null>(null);
    const speciesState = useState<Species[]>([]);
    const wateringPlansState = useState<PotPlansById>({});

    // Weather Forecast refresh upon location change
    const currentLocation = locationState[0];
    useEffect(() => {
        if (currentLocation) {
            getWeather().then((weather) => {
                weatherState[1](weather.forecast);
            }).catch(err => {
                console.error("weather useMemo failed", err);
                toast.error("Couldn't get weather forecast");
            });
        }
    }, [currentLocation]);

    // Load known species once on mount so the Add screen can show them.
    useEffect(() => {
        getSpecies().then((list) => {
            speciesState[1](list);
        }).catch(err => {
            console.error("species fetch failed", err);
            toast.error("Couldn’t load your species");
        });
    }, []);

    return <DataContext.Provider value={{
        potsState, locationState, weatherState, speciesState, wateringPlansState
    }}>
        {children}
    </DataContext.Provider>
}

async function fetchAndStorePotPlan(
    potId: string,
    setPlans: Dispatch<SetStateAction<PotPlansById>>,
    onError: (msg: string) => void,
) {
    setPlans((s) => ({
        ...s,
        [potId]: { plan: s[potId]?.plan ?? null, refreshing: true, error: false },
    }));
    try {
        const plan = await getAssistantWatering(potId);
        setPlans((s) => ({ ...s, [potId]: { plan, refreshing: false, error: false } }));
    } catch (err) {
        console.error("fetchAndStorePotPlan failed", err);
        onError("Couldn’t get watering plan");
        // Keep any previously-good plan (stale-while-error) but flag the failure.
        setPlans((s) => ({
            ...s,
            [potId]: { plan: s[potId]?.plan ?? null, refreshing: false, error: true },
        }));
    }
}

export function usePots() {

    const dataContext = useContext(DataContext);
    if (!dataContext) {
        throw "usePots must be used in DataContext"
    }
    const [pots, setPotsState] = dataContext.potsState
    const [plansById, setPlans] = dataContext.wateringPlansState
    const toast = useToast();

    const plants = useMemo(() => pots.map(pot => pot.plants.map(plant => ({ pot, ...plant }))).flat(), [pots]);

    function invalidatePotIfCached(potId: string) {
        if (!plansById[potId]) return;
        const pot = pots.find((p) => p.id === potId);
        if (!pot || pot.plants.length === 0) return;
        void fetchAndStorePotPlan(potId, setPlans, (m) => toast.error(m));
    }

    function invalidateAllCached() {
        for (const potId of Object.keys(plansById)) {
            invalidatePotIfCached(potId);
        }
    }

    async function addPot(params: Readonly<{
        name: string; shape: string; size?: PotSize; sunStart?: string | null; sunEnd?: string | null
    }>) {
        try {
            const pot = await unwrap(client.api.pots.$post({ json: params }));
            setPotsState((currentPots) => [...currentPots, pot]);
            toast.success("A new pot, ready for roots");
            return pot;
        } catch (err) {
            console.error("addPot failed", err);
            toast.error("Couldn’t create that pot");
            return null;
        }
    }

    async function editPot(id: string, patch: Readonly<Record<string, unknown>>) {
        try {
            const pot = await unwrap(client.api.pots[":id"].$patch({ param: { id }, json: patch }));
            setPotsState((currentPots) => currentPots.map((p) => (p.id === id ? pot : p)));
            invalidatePotIfCached(id);
            toast.success("Pot updated");
            return pot;
        } catch (err) {
            console.error("editPot failed", err);
            toast.error("Couldn’t update that pot");
            return null;
        }
    }

    async function deletePot(id: string) {
        try {
            await expectNoContent(client.api.pots[":id"].$delete({ param: { id } }));
            setPotsState((currentPots) => currentPots.filter((p) => p.id !== id));
            setPlans(({ [id]: _removed, ...rest }) => rest);
            toast.success("Pot removed");
            return true;
        } catch (err) {
            console.error("deletePot failed", err);
            toast.error("Couldn’t delete that pot");
            return false;
        }
    }

    async function setPotWaterEntryForDay(params: Readonly<{ potId: string; date: string, watered: boolean }>) {
        try {
            const updated = params.watered
                ? await unwrap(client.api.pots[":id"].water.$post({
                    param: { id: params.potId },
                    json: { wateredOn: params.date, source: "user" },
                }))
                : await unwrap(client.api.pots[":id"].water[":date"].$delete({
                    param: { id: params.potId, date: params.date },
                }));
            setPotsState((currentPots) => currentPots.map((p) => (p.id === updated.id ? updated : p)));
            invalidatePotIfCached(updated.id);
            return true;
        } catch (err) {
            console.error("setPotWaterEntryForDay failed", err);
            toast.error("Couldn’t update watering");
            return false;
        }
    }

    async function addPlant(payload: Readonly<{
        nickname: string;
        species?: string | null;
        plantedAt: string;
        photoImageId: string | null;
    } & (
            | { potId: string; newPot?: never }
            | { potId?: never; newPot: { name: string; shape: string; size: PotSize; sunStart?: string | null; sunEnd?: string | null } }
        )>) {
        try {
            const pot = await unwrap(client.api.plants.$post({ json: payload }));
            setPotsState((current) => {
                const exists = current.some((p) => p.id === pot.id);
                return exists ? current.map((p) => (p.id === pot.id ? pot : p)) : [...current, pot];
            });
            invalidatePotIfCached(pot.id);
            toast.success("Plant added to your garden");
            return pot;
        } catch (err) {
            console.error("addPlant failed", err);
            toast.error("Couldn’t add that plant");
            return null;
        }
    }

    async function editPlant(id: string, patch: Readonly<Partial<{
        nickname: string;
        species: string | null;
        potId: string;
        plantedAt: string;
        photoImageId: string | null;
    }>>) {
        const previousPot = pots.find((p) => p.plants.some((pl) => pl.id === id));
        try {
            const pot = await unwrap(client.api.plants[":id"].$patch({ param: { id }, json: patch }));
            setPotsState((current) =>
                current.map((p) => {
                    if (p.id === pot.id) return pot;
                    if (previousPot && p.id === previousPot.id) {
                        return { ...p, plants: p.plants.filter((pl) => pl.id !== id) };
                    }
                    return p;
                }),
            );
            invalidatePotIfCached(pot.id);
            if (previousPot && previousPot.id !== pot.id) invalidatePotIfCached(previousPot.id);
            toast.success("Plant updated");
            return pot;
        } catch (err) {
            console.error("editPlant failed", err);
            toast.error("Couldn’t update that plant");
            return null;
        }
    }

    async function deletePlant(id: string) {
        try {
            await expectNoContent(client.api.plants[":id"].$delete({ param: { id } }));
            setPotsState((currentPots) =>
                currentPots.map((pot) => ({
                    ...pot,
                    plants: pot.plants.filter((p) => p.id !== id),
                }))
            );
            invalidateAllCached();
            toast.success("Plant removed");
            return true;
        } catch (err) {
            console.error("deletePlant failed", err);
            toast.error("Couldn’t delete that plant");
            return false;
        }
    }

    return {
        pots,
        plants,
        addPot,
        editPot,
        setPotWaterEntryForDay,
        deletePot,
        addPlant,
        editPlant,
        deletePlant
    }
}

export function useWeather() {
    const dataContext = useContext(DataContext);
    if (!dataContext) {
        throw "usePots must be used in DataContext"
    }
    const [weather] = dataContext.weatherState
    const [location, setLocationState] = dataContext.locationState
    const toast = useToast();

    async function setLocation(params: { location: Location }) {
        try {
            const updatedGarden = await unwrap(client.api.garden.$put({ json: params }));
            toast.success("Garden located");
            setLocationState(updatedGarden.location);
        } catch (err) {
            console.error("setLocation failed", err);
            toast.error("Couldn’t set location for the garden");
            return null;
        }
    }

    return {
        weather,
        location,
        setLocation
    }
}

export function useSpecies() {

    const dataContext = useContext(DataContext);
    if (!dataContext) {
        throw "usePots must be used in DataContext"
    }
    const [species] = dataContext.speciesState;

    return {
        species
    }
}

export function usePotEffectiveSunHours(pot: Pot): Record<TimelineKey, number> | null {
    const dataContext = useContext(DataContext);
    if (!dataContext) {
        throw "usePots must be used in DataContext"
    }
    const [weather] = dataContext.weatherState

    const effectiveSunHours = useMemo(() => {
        if (!weather || !pot.sunHours) {
            return null
        }
        return {
            "twoDaysAgo": computeSunHoursForDay(weather.twoDaysAgo, pot.sunHours?.start, pot.sunHours.end),
            "oneDayAgo": computeSunHoursForDay(weather.oneDayAgo, pot.sunHours?.start, pot.sunHours.end),
            "today": computeSunHoursForDay(weather.today, pot.sunHours?.start, pot.sunHours.end),
            "oneDayLater": computeSunHoursForDay(weather.oneDayLater, pot.sunHours?.start, pot.sunHours.end),
            "twoDaysLater": computeSunHoursForDay(weather.twoDaysLater, pot.sunHours?.start, pot.sunHours.end)
        }
    }, [weather, pot])

    return effectiveSunHours
}

export function usePotGardenerRecommendations(potId: Pot["id"]) {
    const dataContext = useContext(DataContext);
    if (!dataContext) {
        throw "usePots must be used in DataContext"
    }
    const [pots] = dataContext.potsState
    const [plansById, setPlans] = dataContext.wateringPlansState
    const [location] = dataContext.locationState
    const toast = useToast();

    const pot = useMemo(() => pots.find(pot => pot.id === potId), [pots, potId]);
    // Key presence is the "already requested" marker: once an entry exists (even
    // refreshing or errored), we never auto-refetch — only `invalidate*` does.
    const missing = plansById[potId] === undefined;

    useEffect(() => {
        if (!pot) return;
        if (pot.plants.length === 0) return; // empty pots carry no recommendation
        if (!location) return; // watering plans need weather, which needs a location
        if (!missing) return; // already requested/loaded/errored — reuse the shared cache
        void fetchAndStorePotPlan(potId, setPlans, (m) => toast.error(m));
        // `missing` (a boolean), not `plansById`, so unrelated pots' writes don't re-run this.
    }, [potId, pot, missing, location]);

    const entry = plansById[potId];
    return {
        loading: entry?.refreshing ?? false,
        plan: entry?.plan ?? null,
        error: entry?.error ?? false,
    };
}

/** A pot that needs watering today, with the gardener's one-line rationale. */
export type ThirstyPot = { pot: Pot; reason: string | null };

/**
 * Merges every non-empty pot's watering recommendation into a single "to-do for
 * today" view. Ensures each pot's plan is fetched once (shared with the per-pot
 * sections), so the summary card adds no extra LLM calls.
 */
export function useWateringToday() {
    const dataContext = useContext(DataContext);
    if (!dataContext) {
        throw "usePots must be used in DataContext"
    }
    const [pots] = dataContext.potsState
    const [plansById, setPlans] = dataContext.wateringPlansState
    const [location] = dataContext.locationState
    const toast = useToast();

    // The card sits above the pot sections, so it can't rely on them to trigger
    // fetches. Ensure every non-empty pot has a request; key-presence + the
    // in-flight dedupe in `getAssistantWatering` keep this to one fetch per pot.
    useEffect(() => {
        if (!location) return; // watering plans need weather, which needs a location
        for (const pot of pots) {
            if (pot.plants.length === 0) continue;
            if (plansById[pot.id] !== undefined) continue;
            void fetchAndStorePotPlan(pot.id, setPlans, (m) => toast.error(m));
        }
    }, [pots, plansById, location]);

    return useMemo(() => {
        const nonEmpty = pots.filter((p) => p.plants.length > 0);
        const thirsty: ThirstyPot[] = [];
        let anyLoading = false;
        let anyError = false;
        let pending = 0;

        for (const pot of nonEmpty) {
            const entry = plansById[pot.id];
            if (!entry || entry.refreshing) {
                anyLoading = true;
                pending++;
                continue;
            }
            if (entry.error && !entry.plan) {
                anyError = true;
                continue;
            }
            const plan = entry.plan;
            if (!plan) continue;
            // `watered` is "user" | "rain" | undefined — a pot rained on today is done.
            const alreadyWatered = !!pot.timeline.today.watered;
            if (plan.today.shouldWater && !alreadyWatered) {
                thirsty.push({ pot, reason: plan.reason || null });
            }
        }

        return {
            thirsty,
            loading: anyLoading,
            error: anyError,
            totalTracked: nonEmpty.length,
            pending,
            allSettled: pending === 0,
        };
    }, [pots, plansById]);
}
